// apps/web/src/widgets/UnregisteredTemplatePlaceholder.tsx
//
// Phase 11.2 — NET-NEW SURFACE 2 (UI-SPEC). The graceful, neutral affordance the
// dispatcher renders for a templateId that EXISTS in TEMPLATE_CATALOG but has no
// registered renderer yet (the four downstream-owned templates: scaling-matrix →
// 11.4, option-tier-matrix + per-option-detail-card → 11.5,
// shopping-list-aggregator → 11.8).
//
// CONTRACT — this must read as "renders in a later phase, nothing is broken,"
// NEVER as an error. It is deliberately, semantically DISTINCT from the
// WidgetRenderFailure error surface in template-renderer.tsx:
//   - It is NOT an alert role and NOT the error-tone Alert variant.
//   - Neutral chrome only: dashed border-input + muted text (mirrors the
//     EmptyState primitive, apps/web/src/components/ui/empty-state.tsx).
//   - It uses ONLY neutral muted/border tokens — none of the reserved
//     error-tone or rendered-recipe accent tokens (UI-SPEC Color reservation
//     lists).
//   - The "coming later" meaning lives in the rendered TEXT, not in color/icon
//     alone (accessibility); the icon is aria-hidden.
//
// Copy is the UI-SPEC Copywriting Contract verbatim (title = display name +
// "coming in a later phase" suffix; body names the templateId). User-facing
// strings stay generic — the only template/recipe-specific token is the literal
// `templateId` / `name` passed in by the caller (META-07 / Phase-1 C1).
import { Hammer } from "lucide-react";
import type * as React from "react";

export interface UnregisteredTemplatePlaceholderProps {
  /** The cataloged-but-unregistered template id (named in the body for support legibility). */
  templateId: string;
  /** The recipe-chosen display name for this widget instance (drives the title). */
  name?: string;
}

export function UnregisteredTemplatePlaceholder({
  templateId,
  name,
}: UnregisteredTemplatePlaceholderProps): React.ReactElement {
  const displayName = name && name.length > 0 ? name : "This widget";
  return (
    <div
      data-testid="unregistered-template-placeholder"
      className="flex flex-col items-center gap-2 rounded-md border border-dashed border-input p-6 text-center"
    >
      <Hammer className="size-6 text-muted-foreground" aria-hidden />
      <p className="text-base font-semibold leading-tight text-foreground">
        {displayName} — coming in a later phase
      </p>
      <p className="text-sm leading-6 text-muted-foreground">
        This widget ({templateId}) isn't available to render yet. It arrives in a later build phase.
      </p>
    </div>
  );
}
UnregisteredTemplatePlaceholder.displayName = "UnregisteredTemplatePlaceholder";
